import { ClipboardCheck } from 'lucide-react'
import { formatTime12h } from '../constants/hrmsAttendanceDetails.constants'
import type { AttendanceSource } from '../types/hrmsAttendanceRecords.types'
import { AttendanceSourceBadge } from './AttendanceSourceBadge'

interface AttendanceComparisonCardProps {
  variant: 'current' | 'requested'
  checkIn?: string
  checkOut?: string
  oldCheckIn?: string
  oldCheckOut?: string
  source?: AttendanceSource
}

export function AttendanceComparisonCard({
  variant,
  checkIn,
  checkOut,
  oldCheckIn,
  oldCheckOut,
  source,
}: AttendanceComparisonCardProps) {
  if (variant === 'current') {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-3 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
            CURRENT ATTENDANCE
          </span>
          <span className="bg-slate-200 text-slate-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
            Logged
          </span>
        </div>

        <div className="space-y-2.5">
          <div>
            <div className="text-xs text-slate-500">Check In</div>
            <div className="text-base font-bold text-slate-900 mt-0.5">
              {formatTime12h(checkIn)}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-500">Check Out</div>
            <div className="text-base font-bold text-slate-900 mt-0.5">
              {formatTime12h(checkOut)}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 pt-2.5 flex items-center justify-between">
          <span className="text-xs text-slate-500">Source</span>
          <AttendanceSourceBadge source={source || null} />
        </div>
      </div>
    )
  }

  const checkInChanged = checkIn !== undefined && checkIn !== oldCheckIn
  const checkOutChanged = checkOut !== undefined && checkOut !== oldCheckOut

  return (
    <div className="bg-white border-2 border-[#146971] rounded-xl p-3.5 space-y-3 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold text-[#146971] uppercase tracking-wider">
          REQUESTED ATTENDANCE
        </span>
        <span className="bg-teal-50 text-[#146971] border border-teal-200 text-[10px] font-bold px-2 py-0.5 rounded-full">
          Proposed
        </span>
      </div>

      <div className="space-y-2.5">
        <div>
          <div className="text-xs text-slate-500">Check In</div>
          {checkInChanged ? (
            <div className="bg-teal-50/80 border border-teal-200 rounded-lg p-2 flex items-baseline gap-2 flex-wrap mt-1">
              <span className="text-base font-bold text-[#146971]">
                {formatTime12h(checkIn)}
              </span>
              {oldCheckIn && oldCheckIn !== 'none' ? (
                <span className="text-xs text-slate-400 line-through">
                  {formatTime12h(oldCheckIn)}
                </span>
              ) : null}
            </div>
          ) : (
            <div className="text-base font-bold text-slate-900 mt-0.5">
              {formatTime12h(checkIn || oldCheckIn)}
            </div>
          )}
        </div>

        <div>
          <div className="text-xs text-slate-500">Check Out</div>
          {checkOutChanged ? (
            <div className="bg-teal-50/80 border border-teal-200 rounded-lg p-2 flex items-baseline gap-2 flex-wrap mt-1">
              <span className="text-base font-bold text-[#146971]">
                {formatTime12h(checkOut)}
              </span>
              {oldCheckOut && oldCheckOut !== 'none' ? (
                <span className="text-xs text-slate-400 line-through">
                  {formatTime12h(oldCheckOut)}
                </span>
              ) : null}
            </div>
          ) : (
            <div className="text-base font-bold text-slate-900 mt-0.5">
              {formatTime12h(checkOut || oldCheckOut)}
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-slate-100 pt-2.5 flex items-center justify-between">
        <span className="text-xs text-slate-500">Source</span>
        <span className="bg-teal-50 text-teal-800 border border-teal-200 text-xs font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1.5">
          <ClipboardCheck className="h-3.5 w-3.5 text-teal-600 shrink-0" />
          Regularization Request
        </span>
      </div>
    </div>
  )
}
